import { useMemo } from 'react';
import { PersistentRoom, RoomMember } from '../../types/room';
import { RoomCard } from './RoomCard';
import { useUserProfiles } from '../../hooks/useUserProfiles';

interface RoomGridProps {
  rooms: PersistentRoom[];
  onEnterRoom: (room: PersistentRoom) => void;
  onRoomSettings?: (room: PersistentRoom) => void;
  onCreateRoom: () => void;
  isLoading?: boolean;
}

/**
 * Grid of room cards with create button.
 */
export function RoomGrid({
  rooms,
  onEnterRoom,
  onRoomSettings,
  onCreateRoom,
  isLoading = false,
}: RoomGridProps) {
  const memberIds = useMemo(
    () => rooms.flatMap((room) => room.members.map(resolveMemberProfileKey)),
    [rooms]
  );
  const profileMap = useUserProfiles(memberIds);
  const roomAvatars = useMemo(
    () => buildRoomAvatars(rooms, profileMap),
    [rooms, profileMap]
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <RoomCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {rooms.map((room) => (
        <RoomCard
          key={room.id}
          room={room}
          memberAvatars={roomAvatars[room.id]}
          onEnter={onEnterRoom}
          onSettings={onRoomSettings}
        />
      ))}

      <CreateRoomCard onClick={onCreateRoom} />
    </div>
  );
}

/**
 * Builds avatar data for each room card from profile info.
 */
function buildRoomAvatars(
  rooms: PersistentRoom[],
  profileMap: Record<string, { displayName?: string; avatarUrl?: string } | undefined>
): Record<string, { name: string; imageUrl?: string }[]> {
  const result: Record<string, { name: string; imageUrl?: string }[]> = {};

  rooms.forEach((room) => {
    result[room.id] = room.members.map((member) => {
      const profileKey = resolveMemberProfileKey(member);
      const profile = profileMap[profileKey];
      const fallbackName = member.email || 'Member';
      return {
        name: profile?.displayName ?? fallbackName,
        imageUrl: profile?.avatarUrl,
      };
    });
  });

  return result;
}

/**
 * Resolves the profile lookup key for a member.
 */
function resolveMemberProfileKey(member: RoomMember): string {
  if (!member.userId) {
    return member.email;
  }

  if (member.userId.startsWith('invited-') && member.email) {
    return member.email;
  }

  return member.userId;
}

/**
 * Card for creating a new room.
 */
function CreateRoomCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="card card-interactive flex flex-col items-center justify-center min-h-[280px] text-gray-500 hover:text-white group"
    >
      <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 4v16m8-8H4"
          />
        </svg>
      </div>
      <span className="font-medium">Create New Room</span>
    </button>
  );
}

/**
 * Loading skeleton for room card.
 */
function RoomCardSkeleton() {
  return (
    <div className="card flex flex-col overflow-hidden animate-pulse">
      <div className="aspect-[16/10] skeleton" />
      <div className="p-4">
        <div className="h-5 w-2/3 skeleton mb-2" />
        <div className="h-4 w-1/3 skeleton mb-4" />
        <div className="h-10 skeleton rounded-lg" />
      </div>
    </div>
  );
}
